/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {useProductList} from '../util/product-list-context'

// Project Components
import {
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Text,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {PlusIcon, MinusIcon} from '../../../components/custom-icons'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

/**
 * A React functional component that renders a list of category links within an accordion item.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.category - An object containing category data.
 *
 * @returns {JSX.Element} The rendered component.
 */
const CategoryLinks = ({category = {}}) => {
    const {categories = []} = category
    const {onSelect = noop} = useProductList()

    return (
        <AccordionItem as="li" listStyleType="none" border="none" padding={0} key="show-all">
            {({isExpanded}) => (
                <>
                    {/* Using role="heading" instead of a semantic <h2> tag to avoid SEO impact */}
                    <Text as="div" role="heading" aria-level="2">
                        <AccordionButton
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={10}
                            py={20}
                        >
                            <Text as="span" align="left" textStyle="bodyLarge2">
                                <FormattedMessage
                                    defaultMessage="Categories"
                                    id="category_links.button_text"
                                />
                            </Text>
                            {isExpanded ? (
                                <MinusIcon boxSize="12px" />
                            ) : (
                                <PlusIcon boxSize="12px" />
                            )}
                        </AccordionButton>
                    </Text>
                    <AccordionPanel paddingTop={0} paddingBottom={10}>
                        <List display="flex" flexDirection="column" gap={10}>
                            {categories.map(({id, name}) => {
                                return (
                                    <ListItem key={id}>
                                        <Link
                                            key={id}
                                            href={`/category/${id}`}
                                            onClick={onSelect}
                                            useNavLink
                                        >
                                            {name}
                                        </Link>
                                    </ListItem>
                                )
                            })}
                        </List>
                    </AccordionPanel>
                </>
            )}
        </AccordionItem>
    )
}

CategoryLinks.propTypes = {
    category: PropTypes.object
}

export default CategoryLinks
